import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Product } from '@/types'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { getProducts } from '@/lib/products'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { useBag } from '@/components/generals/bag-provider'
import { HeroSection } from '@/components/landing/hero-section'
import { MarqueeBand } from '@/components/landing/marquee-band'
import { CollectionSection } from '@/components/landing/collection-section'
import { StorySection } from '@/components/landing/story-section'
import { SavoirFaireSection } from '@/components/landing/savoir-faire-section'
import { CodesSection } from '@/components/landing/codes-section'
import { VoicesSection } from '@/components/landing/voices-section'
import { CercleSection } from '@/components/landing/cercle-section'
import { ProductQuickView } from '@/components/landing/product-quick-view'

interface HomeProps {
    products: Product[]
}

const Home: FC<HomeProps> = ({ products }): ReactNode => {
    const [viewing, setViewing] = useState<Product | null>(null)
    const { reconcile } = useBag()

    useEffect(() => {
        reconcile(products)
    }, [products, reconcile])

    const featured = useMemo(() => products.find((product) => product.is_featured) ?? products[0] ?? null, [products])
    const categories = useMemo(() => Array.from(new Set(products.map((product) => product.category))), [products])

    const closeViewer = useCallback(() => setViewing(null), [])

    return (
        <Layout>
            <Seo ogImage={featured?.image ?? undefined} />

            <HeroSection product={featured} onView={setViewing} />
            <MarqueeBand />
            <CollectionSection products={products} onView={setViewing} />
            <StorySection />
            <SavoirFaireSection />
            <CodesSection />
            <VoicesSection />
            <CercleSection interests={categories} />

            <ProductQuickView product={viewing} onClose={closeViewer} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const { products } = await getProducts()

    return {
        props: { products },
        revalidate: 60
    }
}

export default Home
